<template>
  <div class="repricing-speed">
    <div class="title">
      <span class="bullet">•</span>
      {{ $t('Dashboard.Banking.InterestRateRisk.RepricingSpeed.Title') }}
    </div>

    <div class="cards-container">
      <!-- Loans Card -->
      <div class="card">
        <div class="card-content">
          <div class="text-section">
            <div class="main-label">
              {{ $t('Dashboard.Banking.InterestRateRisk.RepricingSpeed.Loans') }}
            </div>
            <div class="sub-label">
              {{ $t('Dashboard.Banking.InterestRateRisk.RepricingSpeed.CurrentSpeed') }}
            </div>
          </div>
          <div class="gauge-section">
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <e-chart
                :option="gaugeOption(loansCurrent, loansCurrentColor)"
                class="gauge"
                style="width: 100%; height: 100%; pointer-events:none;"
                renderer="canvas"
                :autoresize="true"
              />
            </v-lazy>
          </div>
        </div>
      </div>

      <!-- Deposits Card -->
      <div class="card">
        <div class="card-content">
          <div class="text-section">
            <div class="main-label">
              {{ $t('Dashboard.Banking.InterestRateRisk.RepricingSpeed.Deposits') }}
            </div>
            <div class="sub-label">
              {{ $t('Dashboard.Banking.InterestRateRisk.RepricingSpeed.CurrentSpeed') }}
            </div>
          </div>
          <div class="gauge-section">
            <v-lazy
              :options="{threshold: .5}"
              transition="fade-transition"
            >
              <e-chart
                :option="gaugeOption(depositsCurrent, depositsCurrentColor)"
                class="gauge"
                style="width: 100%; height: 100%; pointer-events:none;"
                renderer="canvas"
                :autoresize="true"
              />
            </v-lazy>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { API } from '@/api/Api'
import chartsColorMixin from '@/theme/default/components/charts/mixins/chartsColorMixin'
import { use } from 'echarts/core'
import { GaugeChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import RatesResource from '@/api/resources/rates/RatesResource'

use([GaugeChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

export default {
  name     : 'RepricingSpeed',
  mixins   : [chartsColorMixin],
  dataStore: { FindBankRegressionData: 'Rates.FindBankRegressionData' },

  computed: {
    repricing () {
      return this.bankRegressionData?.bank?.repricingSpeed || {}
    },
    loansCurrent () {
      return Number(this.repricing?.loans?.current ?? 0)
    },
    depositsCurrent () {
      return Number(this.repricing?.deposits?.current ?? 0)
    },
    trackColor () { return '#e0e0e0' },
    accountId () {
      return this.user?.selectedAccountId || null
    },
    bankRegressionData () {
      return this.FindBankRegressionData?.Data || null
    },
    loansCurrentColor () { return this.themeColorRGBA('synthesis-ui-green-04') },
    depositsCurrentColor () { return this.themeColorRGBA('synthesis-ui-orange-02') }
  },
  created () {
    this.findBankRegressionData()
  },
  methods: {
    async findBankRegressionData () {
      const response = await API.Resource.Rates.FindBankRegressionData(this.accountId)
      this.handleResponse(response, 'findBankRegressionData')
    },

    handleResponse (response, key) {
      const data = API.responseData(response)?.[key] || null

      const errors = API.responseErrors(response) || []

      if (API.isResponseSuccess(response) && data) {
        this.FindBankRegressionData.Data = RatesResource.fromApiResponse(data)
      } else {
        this.$bus.$emit('app:error', errors)
      }
    },
    gaugeOption (value = 0, mainColor) {
      const v = Math.max(0, Math.min(100, Number(value)))
      const width = 12

      return {
        animation        : true,
        animationDuration: 800,
        color            : mainColor,
        grid             : { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },
        series           : [{
          type      : 'gauge',
          startAngle: 180,
          endAngle  : 0,
          min       : 0,
          max       : 100,
          radius    : '100%',
          center    : ['50%', '70%'],
          progress  : { show: true, roundCap: true, width },
          pointer   : { show: false },
          axisLine  : {
            roundCap : true,
            lineStyle: {
              width,
              color: [
                [v / 100, mainColor],
                [1, this.trackColor]
              ]
            }
          },
          splitLine: { show: false },
          axisTick : { show: false },
          axisLabel: { show: false },
          detail   : {
            show          : true,
            valueAnimation: true,
            offsetCenter  : [0, '-12%'],
            formatter     : (val) =>
              `{val|${Math.round(val)}%}`,
            rich: {
              val: {
                fontSize  : 22,
                fontWeight: 700,
                color     : '#041F30',
                lineHeight: 26
              }
            }
          },
          data: [{ value: v }]
        }]
      }
    }
  }
}
</script>

<style scoped>
.repricing-speed {
  /* padding: 20px; */
  margin-top: 12px;
  background-color: white;
}

.title {
  display: flex;
  align-items: center;
  font-family: Inter, Aeonik, Roboto, sans-serif !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  line-height: 20px !important;
  margin: 0;
  color: var(--v-synthesis-ui-grey-01-base) !important;
  margin-bottom: 20px;
}

.bullet {
  margin-right: 8px;
  color: var(--v-synthesis-ui-grey-01-base) !important;
}

.cards-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.card {
  flex: 1;
  background-color: var(--v-synthesis-ui-grey-06-base);
  border-radius: 12px;
  padding: 25px;
  /* box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1); */
}

.card-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 40px;
}

.text-section {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.main-label {
  color: var(--v-synthesis-brand-dark-blue-base) !important;
  font-family: Aeonik Inter, Roboto, sans-serif;
  font-weight: 500 !important;
  font-style: Medium !important;
  font-size: 20px !important;
  line-height: 130%;
  letter-spacing: 0.2px !important;
}

.sub-label {
  font-family: Inter, Aeonik, Roboto, sans-serif;
  font-size: 12px;
  font-weight: 400;
  color: var(--v-synthesis-ui-grey-01-base) !important;
}

.gauge-section {
  width: 80px;
  height: 60px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gauge {
  width: 100%;
  height: 100%;
}
</style>
